'use client';

import Script from 'next/script';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, Suspense } from 'react';

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}

// ── CAPI helper ───────────────────────────────────────────────────────────────

interface CAPIPayload {
  event_name: string;
  event_source_url?: string;
  user_data?: Record<string, string | undefined>;
  custom_data?: Record<string, unknown>;
}

async function sendCAPIEvent(payload: CAPIPayload): Promise<void> {
  try {
    await fetch('/api/meta-capi/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
      }),
    });
  } catch (err) {
    console.warn('[Meta CAPI] Failed to send event:', err);
  }
}

// ── Event helpers ─────────────────────────────────────────────────────────────

export function trackPageView(userData?: Record<string, string | undefined>) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'PageView');
  }
  sendCAPIEvent({ event_name: 'PageView', user_data: userData });
}

export function trackFormSubmitted(
  params?: Record<string, unknown>,
  userData?: Record<string, string | undefined>
) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'FormSubmitted', params ?? {});
  }
  sendCAPIEvent({ event_name: 'FormSubmitted', custom_data: params, user_data: userData });
}

export function trackBountySubmitted(
  params?: Record<string, unknown>,
  userData?: Record<string, string | undefined>
) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'BountySubmitted', params ?? {});
  }
  sendCAPIEvent({ event_name: 'BountySubmitted', custom_data: params, user_data: userData });
}

export function trackBountyApproved(
  params?: Record<string, unknown>,
  userData?: Record<string, string | undefined>
) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'BountyApproved', params ?? {});
  }
  sendCAPIEvent({ event_name: 'BountyApproved', custom_data: params, user_data: userData });
}

export function trackBountyAccepted(
  params?: Record<string, unknown>,
  userData?: Record<string, string | undefined>
) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'BountyAccepted', params ?? {});
  }
  sendCAPIEvent({ event_name: 'BountyAccepted', custom_data: params, user_data: userData });
}

// ── Route-change PageView tracker ─────────────────────────────────────────────

function RouteChangeTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    trackPageView();
  }, [pathname, searchParams]);

  return null;
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function MetaPixel() {
  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID;

  if (!pixelId) return null;

  return (
    <>
      <Script
        id="meta-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${pixelId}');
            fbq('track', 'PageView');
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
      <Suspense fallback={null}>
        <RouteChangeTracker />
      </Suspense>
    </>
  );
}
